import JSON5 from 'json5';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { CellValue } from '../types';

import styles from './CollapsibleSources.module.scss';
import { DocumentFileLink, isViewableDocument } from './DocumentFileLink';

const parseArray = (value: CellValue): string[] => {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value !== 'string') return [];
  const trimmed = value.trim();
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON5.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch {
      // not parseable as array
    }
  }
  return [trimmed];
};

const Section = ({ filename, content }: { filename: string; content: string }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={styles.section}>
      <button
        type="button"
        className={styles.header}
        onClick={() => setOpen(o => !o)}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className={styles.filename}>{filename}</span>
        {isViewableDocument(filename) && (
          <span
            className={styles.eyeWrap}
            onClick={e => e.stopPropagation()}
          >
            <DocumentFileLink filename={filename} />
          </span>
        )}
      </button>
      {open && (
        <div className={styles.body}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
        </div>
      )}
    </div>
  );
};

interface CollapsibleSourcesProps {
  filenames: CellValue;
  contents: CellValue;
}

export const CollapsibleSources = ({ filenames, contents }: CollapsibleSourcesProps) => {
  const names = parseArray(filenames);
  const bodies = parseArray(contents);

  if (names.length === 0) return null;

  return (
    <div className={styles.wrap}>
      {names.map((name, idx) => (
        <Section
          // eslint-disable-next-line react/no-array-index-key
          key={`${name}-${idx}`}
          filename={name}
          content={bodies[idx] ?? ''}
        />
      ))}
    </div>
  );
};
